import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchGames } from '../../redux/slice/game.slice';
import { addToCart, syncRecentlyViewed } from '../../redux/slice/user.slice';
import { FaWindows } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Autoplay, FreeMode, Mousewheel } from "swiper/modules";
import GameCard, { GameCardSkeleton, PlatformIcon, PlatformColor, PlatformLabel } from '../common/GameCard';
import FloatingRocketAstronautLeft from './FloatingRocketAstronautLeft';
import FloatingRocketAstronaut from './FloatingRocketAstronaut';
import useGridCorners from '../../hooks/useGridCorners';

// ── Horizontal Scroll Row Skeleton ──────────────────────────────────
function ScrollRowSkeleton({ badgeText, titleMain, titleAccent, description }) {
    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ defaultCols: 5 });

    return (
        <div className="w-full animate-pulse">
            <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
                {badgeText && (
                    <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                        {badgeText}
                    </p>
                )}

                <h2 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words">
                    {titleMain}{' '}
                    <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">
                        {titleAccent}
                    </span>
                </h2>

                <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>

                {description && (
                    <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed">
                        {description}
                    </p>
                )}
            </div>

            <div
                ref={setGridRef}
                className="grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 w-full max-w-sm sm500:max-w-none mx-auto py-2 sm:py-3"
            >
                {[...Array(5)].map((_, i) => (
                    <div key={i} className={i >= 4 ? 'hidden 2xl:block' : i >= 3 ? 'hidden lg:block' : i >= 2 ? 'hidden md:block' : i >= 1 ? 'hidden sm500:block' : 'block'}>
                        <GameCardSkeleton style={getCornerStyle(i, 5, gridCols)} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Horizontal Scroll Row ────────────────────────────────────────────
function ScrollRow({
    games,
    onAddToCart,
    badgeText,
    titleMain,
    titleAccent,
    description,
}) {
    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ defaultCols: 5 });

    if (!games || !games.length) return null;

    return (
        <div className="w-full">
            {/* Header Layout (Standardized with Featured Catalog & Categories) */}
            <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
                {badgeText && (
                    <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                        {badgeText}
                    </p>
                )}

                <h2 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words">
                    {titleMain}{' '}
                    <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">
                        {titleAccent}
                    </span>
                </h2>

                <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>

                {description && (
                    <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed">
                        {description}
                    </p>
                )}
            </div>

            {/* Swiper Carousel with Common GameCard (contained within grid width) */}
            <div ref={setGridRef} className="relative group/row w-full overflow-hidden">
                <Swiper
                    modules={[Autoplay, FreeMode, Mousewheel]}
                    autoplay={{ delay: 3500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                    loop={games.length > 5}
                    speed={700}
                    mousewheel={{ forceToAxis: true }}
                    freeMode={true}
                    spaceBetween={16}
                    slidesPerView={1}
                    breakpoints={{
                        0: { slidesPerView: 1, spaceBetween: 16 },
                        500: { slidesPerView: 2, spaceBetween: 16 },
                        768: { slidesPerView: 3, spaceBetween: 20 },
                        1024: { slidesPerView: 4, spaceBetween: 24 },
                        1440: { slidesPerView: 5, spaceBetween: 24 }
                    }}
                    className="w-full py-2 sm:py-3"
                >
                    {games.map((game, index) => (
                        <SwiperSlide key={game.id || game._id} className="!h-auto flex">
                            <div className="w-full h-full">
                                <GameCard
                                    game={game}
                                    onAddToCart={() => onAddToCart(game)}
                                    style={getCornerStyle(index, games.length, gridCols)}
                                />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
}

// ── Main Component ───────────────────────────────────────────────────
export default function RecentlyViewed() {
    const dispatch = useDispatch();
    const { games } = useSelector((s) => s.game);
    const homeData = useSelector((s) => s.home);
    const { isAuthenticated } = useSelector((s) => s.auth);
    const dbRecentIds = useSelector((s) => s.userSlice.recentlyViewed || []);

    const [recentIds, setRecentIds] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('cubegames_recent') || '[]');
        } catch {
            return [];
        }
    });

    // Combined unified games pool from both state.game and preloaded state.home
    const allGamesPool = useMemo(() => {
        const pool = [
            ...(games || []),
            ...(homeData?.trending || []),
            ...(homeData?.newRelease || []),
            ...(homeData?.topSelling || []),
            ...(homeData?.mostPlayed || []),
            ...(homeData?.upcoming || [])
        ];
        const unique = [];
        const seen = new Set();
        for (const g of pool) {
            const id = g?.id || g?._id;
            if (id && !seen.has(String(id))) {
                seen.add(String(id));
                unique.push(g);
            }
        }
        return unique;
    }, [games, homeData]);

    // Platform selector state
    const [platformSelectorGame, setPlatformSelectorGame] = useState(null);
    const [selectedPlatformForGame, setSelectedPlatformForGame] = useState('');

    const handleAddToCartClick = (game) => {
        const available = Object.entries(game.platforms || {}).filter(([_, info]) => info?.available);
        if (available.length === 1) {
            dispatch(addToCart({ ...game, selectedPlatform: available[0][0] }));
        } else if (available.length > 1) {
            setPlatformSelectorGame(game);
            const keys = available.map(a => a[0]);
            setSelectedPlatformForGame(keys.includes('windows') ? 'windows' : keys[0]);
        } else {
            dispatch(addToCart({ ...game, selectedPlatform: 'windows' }));
        }
    };

    // Prefetch lightweight games fallback if home pool is unpopulated
    useEffect(() => {
        const hasHomePool = homeData?.trending && homeData.trending.length > 0;
        if ((!games || games.length === 0) && !hasHomePool) {
            dispatch(fetchGames({ limit: 20 }));
        }
    }, [games, homeData, dispatch]);

    // 1. Sync / Merge local storage and DB on mount/login
    useEffect(() => {
        if (isAuthenticated && dbRecentIds.length > 0) {
            const localIds = JSON.parse(localStorage.getItem('cubegames_recent') || '[]');
            const merged = [...new Set([...localIds, ...dbRecentIds])].slice(0, 10);

            localStorage.setItem('cubegames_recent', JSON.stringify(merged));
            setRecentIds(merged);

            const localStr = JSON.stringify(localIds);
            const dbStr = JSON.stringify(dbRecentIds);
            if (localStr !== dbStr) {
                dispatch(syncRecentlyViewed(merged));
            }
        } else if (isAuthenticated && dbRecentIds.length === 0 && recentIds.length > 0) {
            dispatch(syncRecentlyViewed(recentIds));
        } else if (isAuthenticated && dbRecentIds.length > 0 && recentIds.length === 0) {
            localStorage.setItem('cubegames_recent', JSON.stringify(dbRecentIds));
            setRecentIds(dbRecentIds);
        }
    }, [isAuthenticated, dbRecentIds]);

    // Intercept openGameDetails to track recently viewed
    useEffect(() => {
        const handler = (e) => {
            const id = e.detail?.id;
            if (!id) return;
            setRecentIds((prev) => {
                const filtered = prev.filter((x) => x !== id);
                const next = [id, ...filtered].slice(0, 10);
                localStorage.setItem('cubegames_recent', JSON.stringify(next));

                if (isAuthenticated) {
                    dispatch(syncRecentlyViewed(next));
                }
                return next;
            });
        };

        window.addEventListener('cgGameOpened', handler);
        return () => window.removeEventListener('cgGameOpened', handler);
    }, [isAuthenticated, dispatch]);

    const recentGames = useMemo(() => {
        return recentIds
            .map((id) => allGamesPool.find((g) => (g.id || g._id) === id || String(g.id || g._id) === String(id)))
            .filter(Boolean);
    }, [recentIds, allGamesPool]);

    // Personalized: games sharing categories with recently viewed, excluding already viewed
    const getGameCategoryNames = (g) => {
        if (!g) return [];
        if (g.category) {
            const name = typeof g.category === 'object' ? (g.category.name || g.category._id) : g.category;
            if (name) return [name];
        }
        if (g.categories) {
            return g.categories.map(c => typeof c === 'object' ? (c.name || c._id || c) : c).filter(Boolean);
        }
        return [];
    };

    const finalPersonalized = useMemo(() => {
        const viewedGenres = [...new Set(recentGames.flatMap(g => getGameCategoryNames(g)))];
        const personalized = allGamesPool
            .filter((g) => !recentIds?.includes(g.id || g._id) && !recentIds?.includes(String(g.id || g._id)) && getGameCategoryNames(g).some((c) => viewedGenres?.includes(c)))
            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
            .slice(0, 8);

        const fallback = allGamesPool
            .filter((g) => !recentIds?.includes(g.id || g._id) && !recentIds?.includes(String(g.id || g._id)) && !personalized.some((p) => (p.id || p._id) === (g.id || g._id)))
            .sort((a, b) => (b.rating || 0) - (a.rating || 0));

        return [...personalized, ...fallback].slice(0, 8);
    }, [allGamesPool, recentIds, recentGames]);

    const isHomeLoading = homeData?.loading || (!homeData?.trending?.length && !homeData?.newRelease?.length && (!games || games.length === 0));

    if (allGamesPool.length === 0 || isHomeLoading) {
        return (
            <>
                <FloatingRocketAstronaut />
                <section
                    id="recently-viewed-section"
                    className="py-8 sm:py-12 relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none" />
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ScrollRowSkeleton
                            badgeText="Your History"
                            titleMain="Recently"
                            titleAccent="Viewed"
                            description="Pick up right where you left off. Easily revisit games you've recently explored."
                        />
                    </div>
                </section>

                <FloatingRocketAstronautLeft />
                <section
                    id="picked-for-you-section"
                    className="py-8 sm:py-12 relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none" />
                    <div className="absolute top-[20%] left-[-5%] w-[350px] h-[350px] bg-cyber-cyan/5 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-[15%] right-[-5%] w-[350px] h-[350px] bg-cyber-purple/5 rounded-full blur-3xl pointer-events-none" />

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ScrollRowSkeleton
                            badgeText="Tailored Catalog"
                            titleMain="Picked"
                            titleAccent="For You"
                            description="Handpicked releases and trending titles curated to match your gaming taste."
                        />
                    </div>
                </section>
            </>
        );
    }

    if (recentGames.length === 0 && finalPersonalized.length === 0) return null;

    return (
        <>
            <FloatingRocketAstronaut />

            {/* ── Recently Viewed ── */}
            {recentGames.length > 0 && (
                <section
                    id="recently-viewed-section"
                    className="py-8 sm:py-12 relative overflow-hidden"
                >
                    {/* Background Cyber Grid & Neon Glow Halos */}
                    <div className="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none" />
                    <div className="absolute top-[20%] left-[-5%] w-[350px] h-[350px] bg-cyber-cyan/5 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-[15%] right-[-5%] w-[350px] h-[350px] bg-cyber-purple/5 rounded-full blur-3xl pointer-events-none" />

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ScrollRow
                            games={recentGames}
                            onAddToCart={handleAddToCartClick}
                            badgeText="Your History"
                            titleMain="Recently"
                            titleAccent="Viewed"
                            description="Pick up right where you left off. Easily revisit games you've recently explored."
                        />
                    </div>
                </section>
            )}

            <FloatingRocketAstronautLeft />

            {/* ── Personalized Picked For You ── */}
            {finalPersonalized.length > 0 && (
                <section
                    id="picked-for-you-section"
                    className="py-8 sm:py-12 relative overflow-hidden"
                >
                    {/* Background Cyber Grid & Neon Glow Halos */}
                    <div className="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none" />
                    <div className="absolute top-[20%] right-[-5%] w-[450px] h-[450px] bg-cyber-cyan/5 rounded-full blur-[150px] pointer-events-none" />
                    <div className="absolute bottom-[15%] left-[-5%] w-[450px] h-[450px] bg-cyber-purple/5 rounded-full blur-[150px] pointer-events-none" />

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ScrollRow
                            games={finalPersonalized}
                            onAddToCart={handleAddToCartClick}
                            badgeText="Tailored Catalog"
                            titleMain="Picked"
                            titleAccent="For You"
                            description="Handpicked releases and trending titles curated to match your gaming taste."
                        />
                    </div>
                </section>
            )}

            {/* Platform Selector Modal */}
            {platformSelectorGame && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-md bg-[#0c0c18] border border-cyber-cyan/30 rounded-[2.5rem] p-8 shadow-[0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink"></div>

                        <div className="space-y-6">
                            <div className="text-center space-y-2">
                                <h3 className="text-xl font-cyber font-black text-white uppercase tracking-tight">Select Platform</h3>
                                <p className="text-gray-400 text-xs font-medium">
                                    Choose your target platform for{" "}
                                    <span className="text-cyber-cyan">{platformSelectorGame.title}</span>
                                </p>
                            </div>

                            <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                                {Object.entries(platformSelectorGame.platforms || {})
                                    .filter(([_, info]) => info?.available)
                                    .map(([key, info]) => {
                                        const isSelected = selectedPlatformForGame === key;
                                        const Icon = PlatformIcon[key] || FaWindows;
                                        const label = PlatformLabel[key] || key;
                                        const brandColor = PlatformColor[key] || '#9ca3af';
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setSelectedPlatformForGame(key)}
                                                className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left
                                                    ${isSelected
                                                        ? 'bg-cyber-cyan/10 border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]'
                                                        : 'bg-white/[0.02] border-white/5 text-gray-400 hover:text-white hover:bg-white/[0.04]'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon className="w-4 h-4" style={{ color: brandColor }} />
                                                    <span className="text-xs font-cyber font-bold uppercase tracking-wide">{label}</span>
                                                </div>
                                                <span className="text-xs font-mono font-bold text-white">
                                                    {info.price === 0 ? 'FREE' : `$${info.price}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { setPlatformSelectorGame(null); setSelectedPlatformForGame(''); }}
                                    className="flex-1 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        dispatch(addToCart({ ...platformSelectorGame, selectedPlatform: selectedPlatformForGame }));
                                        setPlatformSelectorGame(null);
                                        setSelectedPlatformForGame('');
                                    }}
                                    disabled={!selectedPlatformForGame}
                                    className="flex-1 py-3.5 rounded-2xl bg-cyber-cyan/20 border border-cyber-cyan/40 text-cyber-cyan font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-cyan/30 disabled:opacity-40 transition-all"
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
